import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Bell, 
  BellRing, 
  X, 
  Clock, 
  AlertCircle, 
  AlertTriangle, 
  MoreVertical, 
  Check, 
  CheckCheck, 
  Car, 
  Wrench, 
  Shield, 
  CreditCard, 
  Settings, 
  CheckCircle,
  RefreshCw
} from 'lucide-react';
import i18n from '../../lib/i18n';
import { useNotifications } from '../../hooks/useNotifications';
import { Button } from './button';
import { Badge } from './badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './card';
import { Separator } from './separator';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from './dropdown-menu';
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from './popover';
import { ScrollArea } from './scroll-area';
import { formatDistanceToNow } from 'date-fns';
import { ar, enUS, fr } from 'date-fns/locale';
import { cn } from '../../lib/utils';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../store/authStore';
import { NotificationActions } from '../../utils/notificationActions';

interface NotificationDropdownProps {
  onOpenBookingModal?: (bookingId: string) => void;
  onOpenVehicleModal?: (vehicleId: string) => void;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ 
  onOpenBookingModal, 
  onOpenVehicleModal 
}) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [processingAction, setProcessingAction] = useState<string | null>(null);
  const [carImages, setCarImages] = useState<{ [key: string]: string }>({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { user } = useAuthStore();
  const { 
    notifications, 
    unreadCount, 
    loading, 
    error,
    markAsRead, 
    markAllAsRead, 
    dismissNotification,
    fetchNotifications
  } = useNotifications();
  
  // Track if dropdown is open for refresh management
  const isOpenRef = useRef(false);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // RTL Detection for Arabic language
  const isRTL = i18n.language === 'ar';

  const dateLocale = i18n.language === 'ar' ? ar : i18n.language === 'fr' ? fr : enUS;



  // Manual refresh functionality
  const handleManualRefresh = useCallback(async () => {
    if (isRefreshing) return;
    
    console.log('Starting manual refresh');
    setIsRefreshing(true);
    try {
      await fetchNotifications(false); // Silent update
      console.log('Manual refresh completed successfully');
    } catch (error) {
      console.error('Manual refresh failed:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchNotifications, isRefreshing]);

  // Auto-refresh when dropdown is open
  useEffect(() => {
    isOpenRef.current = isOpen;
    
    if (isOpen) {
      // Refresh immediately when dropdown opens (silent)
      fetchNotifications(false);
      
      // Set up auto-refresh every 10 seconds while open
      const startAutoRefresh = () => {
        if (refreshTimeoutRef.current) {
          clearTimeout(refreshTimeoutRef.current);
        }
        
        refreshTimeoutRef.current = setTimeout(() => {
          if (isOpenRef.current) {
            fetchNotifications(false); // Silent update
            startAutoRefresh(); // Continue the cycle
          }
        }, 10000); // 10 seconds
      };
      
      startAutoRefresh();
    } else {
      // Clear auto-refresh when dropdown closes
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    }
    
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [isOpen, handleManualRefresh]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  // Get priority icon with enhanced styling
  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return <AlertTriangle className="h-4 w-4 text-destructive animate-pulse" />;
      case 'important':
        return <AlertCircle className="h-4 w-4 text-orange-500" />;
      default:
        return <Bell className="h-4 w-4 text-muted-foreground" />;
    }
  };

  // Get type icon instead of badge
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'booking':
        return <Car className="h-3 w-3 text-blue-500" />;
      case 'maintenance':
        return <Wrench className="h-3 w-3 text-orange-500" />;
      case 'insurance':
        return <Shield className="h-3 w-3 text-green-500" />;
      case 'payment':
        return <CreditCard className="h-3 w-3 text-purple-500" />;
      default:
        return <Settings className="h-3 w-3 text-muted-foreground" />;
    }
  };

  // Fetch car images for notifications
  const fetchCarImages = useCallback(async () => {
    if (!notifications.length) return;

    const vehicleIds = notifications
      .map(n => n.car_id)
      .filter(Boolean)
      .filter(id => !carImages[id]); // Only fetch if not already cached

    if (!vehicleIds.length) return;

    try {
      const { data: vehicles, error } = await supabase
        .from('cars')
        .select('id, images, make, model')
        .in('id', vehicleIds);

      if (!error && vehicles) {
        const newCarImages: { [key: string]: string } = {};
        
        vehicles.forEach(vehicle => {
          // Get the first image from the vehicle's images array
          const firstImage = vehicle.images && vehicle.images.length > 0 
            ? vehicle.images[0] 
            : null;
          
          if (firstImage) {
            newCarImages[vehicle.id] = firstImage;
          }
        });

        setCarImages(prev => ({ ...prev, ...newCarImages }));
      }
    } catch (error) {
      console.error('Error fetching car images:', error);
    }
  }, [notifications, carImages]);

  // Fetch car images when notifications change
  useEffect(() => {
    fetchCarImages();
  }, [fetchCarImages]);

  // Get car image for notification
  const getCarImage = (notification: any) => {
    // Check if notification has car data
    const vehicleId = notification.car_id;
    
    if (!vehicleId) {
      return null;
    }

    // Get the car image from cache
    const carImageUrl = carImages[vehicleId];
    
    return (
      <div className="flex-shrink-0 relative">
        {carImageUrl ? (
          <img 
            src={carImageUrl} 
            alt="Vehicle"
            className="w-12 h-8 object-cover rounded-md border border-border/50 shadow-sm"
            onError={(e) => {
              // Fallback to a default car icon if image fails to load
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              target.nextElementSibling?.classList.remove('hidden');
            }}
          />
        ) : null}
        <div className={`${carImageUrl ? 'hidden' : 'flex'} w-12 h-8 bg-muted rounded-md border border-border/50 items-center justify-center`}>
          <Car className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
    );
  };

  // Auto-dismiss resolved notifications
  const checkAndDismissResolvedNotifications = useCallback(async () => {
    if (!user || notifications.length === 0) return;

    try {
      for (const notification of notifications) {
        let shouldDismiss = false;

        // Check booking-related notifications
        if (notification.booking_id && notification.type === 'booking') {
          const { data: booking, error } = await supabase
            .from('bookings')
            .select('status, end_date, return_time')
            .eq('id', notification.booking_id)
            .single();

          if (!error && booking) {
            // Dismiss if booking is completed
            if (booking.status === 'completed') {
              shouldDismiss = true;
            }
            // Dismiss overdue notifications if booking is no longer overdue
            else if (notification.metadata?.notification_type === 'return_overdue' || 
                     notification.metadata?.notification_type === 'return_critical') {
              const returnDateTime = new Date(booking.end_date + 'T' + booking.return_time);
              const now = new Date();
              if (now <= returnDateTime) {
                shouldDismiss = true;
              }
            }
          }
        }

        // Check car-related notifications
        if (notification.car_id && notification.type === 'maintenance') {
          const { data: car, error } = await supabase
            .from('cars')
            .select('insurance_expiry_date, technical_control_expiry_date, last_oil_change_date, current_mileage, oil_change_interval_km, updated_at, created_at')
            .eq('id', notification.car_id)
            .single();

          if (!error && car) {
            const now = new Date();
            
            // Check if insurance has been renewed
            if (notification.title.includes('Insurance') && car.insurance_expiry_date) {
              const insuranceExpiry = new Date(car.insurance_expiry_date);
              const daysUntilExpiry = Math.ceil((insuranceExpiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
              
              // If insurance date is more than 30 days in the future, dismiss the old notification
              // This handles cases where user updated insurance to next year
              if (daysUntilExpiry > 30) {
                shouldDismiss = true;
              }
              
              // Also dismiss if the notification was created before the current insurance date was set
              // This ensures old notifications are cleared when insurance is renewed
              const notificationCreated = new Date(notification.created_at);
              const insuranceUpdated = new Date(car.updated_at || car.created_at);
              if (insuranceUpdated > notificationCreated) {
                shouldDismiss = true;
              }
            }

            // Check if technical control has been renewed
            if (notification.title.includes('Technical Control') && car.technical_control_expiry_date) {
              const techExpiry = new Date(car.technical_control_expiry_date);
              const daysUntilExpiry = Math.ceil((techExpiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
              
              // If technical control date is more than 30 days in the future, dismiss the old notification
              if (daysUntilExpiry > 30) {
                shouldDismiss = true;
              }
              
              // Also dismiss if the notification was created before the current technical control date was set
              const notificationCreated = new Date(notification.created_at);
              const techUpdated = new Date(car.updated_at || car.created_at);
              if (techUpdated > notificationCreated) {
                shouldDismiss = true;
              }
            }

            // Check if oil change has been done
            if (notification.title.includes('Oil Change') && car.last_oil_change_date && car.current_mileage && car.oil_change_interval_km) {
              const lastOilChange = new Date(car.last_oil_change_date);
              const daysSinceLastOilChange = Math.ceil((now.getTime() - lastOilChange.getTime()) / (1000 * 60 * 60 * 24));
              
              // If oil change was done recently (within 7 days), dismiss the notification
              if (daysSinceLastOilChange <= 7) {
                shouldDismiss = true;
              }
              
              // Also dismiss if the notification was created before the oil change date was updated
              const notificationCreated = new Date(notification.created_at);
              const oilChangeUpdated = new Date(car.updated_at || car.created_at);
              if (oilChangeUpdated > notificationCreated) {
                shouldDismiss = true;
              }
            }
          }
        }

        // Auto-dismiss if resolved
        if (shouldDismiss) {
          await dismissNotification(notification.id);
        }
      }
    } catch (error) {
      console.error('Error checking resolved notifications:', error);
    }
  }, [user, notifications, dismissNotification]);

  // Run auto-dismiss check periodically
  useEffect(() => {
    const interval = setInterval(checkAndDismissResolvedNotifications, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [checkAndDismissResolvedNotifications]);



  // Handle notification click with enhanced logic
  const handleNotificationClick = async (notification: any) => {
    // Mark as read if unread
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }

    // Priority 1: Handle booking-related notifications (by type or content)
    if (notification.type === 'booking' || 
        notification.booking_id || 
        notification.title.includes('Pickup') ||
        notification.title.includes('Return') ||
        notification.title.includes('Overdue') ||
        notification.title.includes('Booking') ||
        notification.metadata?.notification_type?.includes('pickup') ||
        notification.metadata?.notification_type?.includes('return')) {
      
      if (notification.booking_id && onOpenBookingModal) {
        onOpenBookingModal(notification.booking_id);
        setIsOpen(false);
        return;
      }
    }

    // Priority 2: Handle car-related notifications (maintenance, insurance, etc.)
    if ((notification.type === 'maintenance' || 
         notification.title.includes('Insurance') ||
         notification.title.includes('Technical Control') ||
         notification.title.includes('Oil Change') ||
         notification.title.includes('Maintenance')) && 
        notification.car_id && onOpenVehicleModal) {
      onOpenVehicleModal(notification.car_id);
      setIsOpen(false);
      return;
    }

    // Fallback: Default behavior
    setIsOpen(false);
  };

  const handleMarkAllRead = () => {
    markAllAsRead();
  };

  const handleDismiss = (notificationId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    dismissNotification(notificationId);
  };

  // Handle "Mark as Returned" action for return reminder notifications
  const handleMarkAsReturned = async (notification: any, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!notification.booking_id) {
      console.error('No booking ID found for return notification');
      return;
    }

    setProcessingAction(notification.id);
    
    try {
      const result = await NotificationActions.markBookingAsReturned(notification.booking_id);
      
      if (result.success) {
        // Show success message (you can implement toast notifications here)
        console.log('Success:', result.message);
        
        // Refresh notifications to reflect the change
        await fetchNotifications();
        
        // Mark notification as read and dismissed
        await dismissNotification(notification.id);
      } else {
        console.error('Failed to mark as returned:', result.error);
        // Show error message (you can implement toast notifications here)
      }
    } catch (error) {
      console.error('Error marking as returned:', error);
    } finally {
      setProcessingAction(null);
    }
  };

  const formatRelativeTime = (date: string) => {
    try {
      return formatDistanceToNow(new Date(date), { 
        addSuffix: true, 
        locale: dateLocale 
      });
    } catch (error) {
      return new Date(date).toLocaleString();
    }
  };

  // Check if notification is a return reminder that needs action button
  const isReturnReminderNotification = (notification: any) => {
    return (
      notification.booking_id &&
      notification.type === 'booking' &&
      (notification.title.includes('Return') || 
       notification.title.includes('Overdue') ||
       notification.metadata?.notification_type?.includes('return')) &&
      !notification.action_taken
    );
  };

  // Check if notification is a maintenance-related notification
  const isMaintenanceNotification = (notification: any) => {
    return (
      notification.car_id &&
      (notification.type === 'maintenance' || 
       notification.type === 'insurance' ||
       notification.title.includes('Insurance') ||
       notification.title.includes('Technical Control') ||
       notification.title.includes('Oil Change') ||
       notification.title.includes('Maintenance'))
    );
  };

  // Enhanced notification item with better visual hierarchy
  const NotificationItem = ({ notification, index }: { notification: any, index: number }) => (
    <div key={notification.id}>
      <div
        className={cn(
          "group relative p-3 rounded-lg cursor-pointer transition-all duration-200",
          !notification.is_read && `bg-primary/5 ${isRTL ? 'border-r-4 border-primary' : 'border-l-4 border-primary'}`,
          "hover:bg-muted/50 hover:shadow-sm",
          notification.priority === 'urgent' && `${isRTL ? 'border-r-4 border-destructive' : 'border-l-4 border-destructive'} bg-destructive/5`
        )}
        onClick={() => handleNotificationClick(notification)}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="flex-shrink-0 mt-0.5">
            {getPriorityIcon(notification.priority)}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className={`flex items-start justify-between mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <h4 className={cn(
                "text-sm font-medium leading-tight line-clamp-2",
                isRTL ? "pl-2 text-right" : "pr-2 text-left",
                !notification.is_read && "font-semibold text-foreground",
                notification.is_read && "text-foreground/80"
              )}>
                {notification.title}
              </h4>
              
              <div className={`flex items-center gap-2 flex-shrink-0 ${isRTL ? 'flex-row-reverse' : ''}`}>
                {getCarImage(notification)}
              </div>
            </div>
            
            <p className={`text-sm text-muted-foreground mb-2 line-clamp-3 ${isRTL ? 'text-right' : 'text-left'}`}>
              {notification.message}
            </p>
            
            <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
              {/* Time ago on the left/right */}
              <div className={`flex items-center gap-1 text-xs text-muted-foreground ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Clock className="h-3 w-3" />
                {formatRelativeTime(notification.created_at)}
              </div>
              
              {/* Action buttons on the right/left */}
              {(isReturnReminderNotification(notification) || isMaintenanceNotification(notification)) && (
                <div className={`flex gap-2 ${isRTL ? 'justify-start' : 'justify-end'}`}>
                  {isReturnReminderNotification(notification) && (
                    <Button
                      size="sm"
                      variant="default"
                      className="h-6 px-2 bg-green-600 hover:bg-green-700 text-white text-xs"
                      onClick={(e) => handleMarkAsReturned(notification, e)}
                      disabled={processingAction === notification.id}
                    >
                      {processingAction === notification.id ? (
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 border border-white border-t-transparent rounded-full animate-spin" />
                          <span>{t('common.processing') || 'Processing...'}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          <span>{t('agency.notifications.markAsReturned') || 'Mark as Returned'}</span>
                        </div>
                      )}
                    </Button>
                  )}
                  
                  {isMaintenanceNotification(notification) && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 px-2 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (notification.car_id && onOpenVehicleModal) {
                          onOpenVehicleModal(notification.car_id);
                          setIsOpen(false);
                        }
                      }}
                    >
                      <div className="flex items-center gap-1">
                        <Wrench className="h-3 w-3" />
                        <span>{t('agency.notifications.editVehicle') || 'Edit Vehicle'}</span>
                      </div>
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {!notification.is_read && (
          <div className={`absolute ${isRTL ? 'left-2' : 'right-2'} top-2 w-2 h-2 bg-primary rounded-full`}></div>
        )}
      </div>
      
      {index < notifications.length - 1 && (
        <Separator className="my-1" />
      )}
    </div>
  );

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon" 
          className="relative justify-center gap-2 whitespace-nowrap text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 h-9 flex items-center space-x-2 rtl:space-x-reverse font-medium hover:text-brand-600 dark:hover:text-brand-400 bg-brand-500/20 hover:bg-brand-500/30 dark:bg-brand-900/20 dark:hover:bg-brand-950/30 transition-all duration-300 rounded-lg p-2"
          aria-label={t('common.notifications.title')}
        >
          {unreadCount > 0 ? (
            <BellRing className="h-4 w-4 text-primary" />
          ) : (
            <Bell className="h-4 w-4" />
          )}
          
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs animate-pulse"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent 
        className="w-96 p-0 border-0 shadow-2xl" 
        align={isRTL ? 'start' : 'end'}
        sideOffset={8}
      >
        <Card className="border-0 shadow-none" dir={isRTL ? 'rtl' : 'ltr'}>
          <CardHeader className="pb-3 border-b">
            <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
              <CardTitle className={`text-lg font-semibold ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('common.notifications.title')}
              </CardTitle>
              <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                {unreadCount > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {t('common.notifications.unreadCount', { count: unreadCount })}
                  </Badge>
                )}

                {/* Refresh Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleManualRefresh}
                  disabled={isRefreshing}
                  className="h-8 w-8 p-0"
                  title={t('common.refresh')}
                >
                  <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                </Button>


                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsOpen(false)}
                  className="h-8 w-8 p-0 hover:bg-muted/50"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="p-0">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
                <p className="text-xs text-muted-foreground">Loading notifications...</p>
              </div>
            ) : error ? (
              <div className={`flex flex-col items-center justify-center py-12 text-muted-foreground ${isRTL ? 'text-center' : 'text-center'}`}>
                <AlertCircle className="h-12 w-12 mb-4 opacity-50 text-red-500" />
                <p className={`text-sm font-medium ${isRTL ? 'text-center' : 'text-center'}`}>Error loading notifications</p>
                <p className={`text-xs mt-1 ${isRTL ? 'text-center' : 'text-center'}`}>{error}</p>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => fetchNotifications(false)}
                  className="mt-2"
                >
                  Try Again
                </Button>
              </div>
            ) : notifications.length === 0 ? (
              <div className={`flex flex-col items-center justify-center py-12 text-muted-foreground ${isRTL ? 'text-center' : 'text-center'}`}>
                <Bell className="h-12 w-12 mb-4 opacity-50" />
                <p className={`text-sm font-medium ${isRTL ? 'text-center' : 'text-center'}`}>{t('common.notifications.empty')}</p>
                <p className={`text-xs mt-1 ${isRTL ? 'text-center' : 'text-center'}`}>{t('common.notifications.emptyDescription')}</p>
              </div>
            ) : (
              <>
                <ScrollArea className="h-[450px]">
                  <div className="px-4 py-2">
                    {notifications.map((notification, index) => (
                      <NotificationItem 
                        key={notification.id} 
                        notification={notification} 
                        index={index} 
                      />
                    ))}
                  </div>
                </ScrollArea>
                
                {unreadCount > 0 && (
                  <>
                    <Separator />
                    <div className="p-4 bg-muted/30">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleMarkAllRead}
                        className={`w-full justify-center hover:bg-muted/50 ${isRTL ? 'flex-row-reverse' : ''}`}
                      >
                        <CheckCheck className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                        {t('common.notifications.markAllRead')}
                      </Button>
                    </div>
                  </>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationDropdown; 